import { editorSession } from "@lureo/editor-bridge";
import { createBridgeValidator, type ContractDocuments } from "@lureo/editor-contracts";
import { initErrorReporting } from "./errorReporting";
import { startHostBridge, type HostCanvas, type HostPage } from "./hostBridge";
import { showsHumanoidDebug } from "./hostEnvironment";
import { SIGHTING_PLATFORM, sightingClient, startReleaseSighting } from "./releaseSighting";
import { readRuntimeConfig, readSentryDsn, type HostConfigWindow } from "./runtimeConfig";

// The release version, its build number and the commit (vite.config.ts); "0" is not a release build.
declare const __PRODUCT_VERSION__: string;
declare const __BUILD_NUMBER__: string;
declare const __SOURCE_COMMIT__: string;
// The contracts in the dev server, null in a build (vite.config.ts).
declare const __LUREO_CONTRACTS__: ContractDocuments | null;

const configWindow = window as unknown as HostConfigWindow;

void initErrorReporting({
  dsn: readSentryDsn(configWindow),
  hostname: window.location.hostname,
  version: __PRODUCT_VERSION__,
  buildNumber: __BUILD_NUMBER__,
  load: () => import("@sentry/browser"),
  log: (message, detail) => {
    console.log(message, detail);
  },
});

const startupConfig = readRuntimeConfig(configWindow);
if (startupConfig !== null && __BUILD_NUMBER__ !== "0") {
  startReleaseSighting(
    { platform: SIGHTING_PLATFORM, version: __PRODUCT_VERSION__, build: __BUILD_NUMBER__, sha: __SOURCE_COMMIT__ },
    sightingClient({ ...startupConfig, fetch: (input, init) => window.fetch(input, init) }),
  );
}

declare global {
  interface Window {
    lureoEditorJwt?: () => string | null;
  }
}

const element = (selector: string): HTMLElement => {
  const found = document.querySelector<HTMLElement>(selector);
  if (found === null) throw new Error(`index.html has no ${selector}`);
  return found;
};

const canvas = element("#unity-canvas") as HTMLCanvasElement;

const page: HostPage = {
  href: () => window.location.href,
  origin: window.location.origin,
  referrer: document.referrer,
  parent: window.parent !== window ? window.parent : null,
  replaceUrl: (url) => {
    window.history.replaceState({}, "", url);
  },
  addMessageListener: (listener) => {
    window.addEventListener("message", listener);
  },
  addDocumentKeyListener: (listener) => {
    document.addEventListener("keydown", (event) => {
      listener(event);
    });
  },
  addDocumentPointerListener: (type, listener) => {
    document.addEventListener(type, listener, true);
  },
  setTimeout: (callback, milliseconds) => {
    window.setTimeout(callback, milliseconds);
  },
  findCanvas: (): HostCanvas | null =>
    document.querySelector<HTMLCanvasElement>("canvas#unity-canvas") ?? document.querySelector("canvas"),
  exposeEditorJwt: (read) => {
    window.lureoEditorJwt = read;
  },
  loadingBar: element("#unity-loading-bar"),
  progressBarFull: element("#unity-progress-bar-full"),
  warningBanner: element("#unity-warning"),
};

startHostBridge(page, {
  productVersion: __PRODUCT_VERSION__,
  session: editorSession,
  loadLoader: async () => {
    const { createEditorInstance } = await import("./editorInstance");
    const contractCheck = __LUREO_CONTRACTS__ === null ? undefined : createBridgeValidator(__LUREO_CONTRACTS__);
    return {
      createInstance: (onProgress) => {
        const config = readRuntimeConfig(configWindow);
        if (config === null) return Promise.reject(new Error("No runtime config: /config.js sets no backend URL or API key"));
        return createEditorInstance(canvas, { ...config, humanoidDebug: showsHumanoidDebug(window.location.hostname) }, onProgress, contractCheck);
      },
    };
  },
});
